import { useEffect, useRef, useState } from 'react';

export interface RowAction {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Renders in the destructive colour and sits last, separated. */
  danger?: boolean;
  /** Shown as the item's title — say why it is unavailable rather than leaving it inert. */
  title?: string;
}

/**
 * The four per-row buttons, collapsed behind one control.
 *
 * Four `btn-sm`s need about 10.5% of the KT Insights table to sit on two lines, and that width was
 * the only place a Head office column could come from. Actions are also the one thing in a row
 * nobody scans — they are used after you have found the row, not to find it — so they are what
 * should give way.
 *
 * Closes on outside click (mousedown, so it beats the row's own click handler), on Escape, and
 * after any selection. Every click inside stops propagation because these rows navigate.
 */
export function RowActionsMenu({ actions, ariaLabel, busy }: {
  actions: RowAction[];
  /** Names the row, so a screen reader hears "Actions for Travel Spend" rather than nine "Actions". */
  ariaLabel: string;
  busy?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  /*
   * The menu is positioned against the VIEWPORT, not the cell, because the cell sits inside a
   * scroll container: `.card:has(> .data-table)` sets overflow-x, and CSS computes the other axis
   * to auto whenever one axis is not visible — so an absolutely positioned menu would be clipped
   * by the card, which on the last row of a table is exactly where the whole menu is.
   *
   * The trade is that a fixed position does not follow the page, so any scroll or resize closes it.
   * That is the conventional behaviour for a row menu and cheaper than tracking the trigger.
   */
  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);
    // Capture phase: the card's own scroll does not bubble, and it is the one most likely to move.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  function toggle() {
    if (open) { setOpen(false); return; }
    const r = triggerRef.current?.getBoundingClientRect();
    // Right-aligned to the trigger: this is the last column, so a menu growing rightwards would
    // leave the window.
    if (r) setAt({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  }

  function close(returnFocus: boolean) {
    setOpen(false);
    // Back to the trigger on Escape, so keyboard users are not dropped at the top of the document.
    if (returnFocus) triggerRef.current?.focus();
  }

  return (
    <div
      className="row-actions"
      ref={boxRef}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); close(true); } }}
    >
      {/* A glyph, not the word "Actions". The column this has to fit in is 5.5% of a 1150px table —
          63px — and "Actions ▾" wants about 83px at .8rem, so the word would wrap or overflow. The
          ellipsis is the conventional row-menu affordance and the accessible name carries the
          meaning. */}
      <button
        ref={triggerRef}
        type="button"
        className="row-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={ariaLabel}
        disabled={busy}
        onClick={toggle}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {open && at && (
        <div className="row-actions-menu" role="menu" style={{ top: at.top, right: at.right }}>
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              className={`row-actions-item${a.danger ? ' row-actions-item-danger' : ''}`}
              disabled={a.disabled}
              title={a.title}
              onClick={() => { close(false); a.onSelect(); }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
